import { type ReactNode, useMemo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';

import { useNavigate, useRouteLoaderData, useSearchParams } from '@remix-run/react';
import type { Tier as DreamTier, StripeCardElementComponent, TPricingElement } from '@shared/dream-components';
import { currencyFormatter, PricingElement } from '@shared/dream-components';
import { CardElement, Elements, PaymentElement, PaymentRequestButtonElement } from '@stripe/react-stripe-js';
import { produce } from 'immer';

import SignupAction from '~/components/SignupAction';
import Text from '~/components/Text';
import UpgradeAction from '~/components/UpgradeAction';
import { UpgradeContext } from '~/context/UpgradeContext';
import { UpgradeProvider } from '~/context/UpgradeProvider';
import usePricingDetails from '~/hooks/useLoaders/usePricingDetails';
import useSignupRedirects from '~/hooks/useUtils/useSignupRedirects';
import type { PricingDetails, Price as SwarmPrice, Tier as SwarmTier } from '~/interfaces/subscriptions';
import type { RootLoaderData } from '~/root';

interface Props {
  node: TPricingElement;
  children: ReactNode | ReactNode[];
}

const mapTier = (
  tier: DreamTier,
  validTiers: Record<string, SwarmTier>,
  validPricesMap: Record<string, SwarmPrice>,
) => {
  if (!(tier.id in validTiers)) {
    return tier;
  }

  return {
    ...tier,
    prices: tier.prices.map((price) => {
      const livePrice = validPricesMap[price.id];
      if (!livePrice) return price;

      return {
        ...price,
        amount_cents: livePrice.amount_cents,
        original_amount_cents: livePrice.original_amount_cents,
        currency: livePrice.currency,
        interval: livePrice.interval,
        cta: livePrice.cta,
        denominator: livePrice.denominator,
        features: livePrice.features || [],
        premium_offer: livePrice.premium_offer,
      };
    }),
  };
};

// Swap tiers out that might not be applicable to the current subscriber
// based on their subscription
const swapTiersInNode = (node: TPricingElement, pricingData: PricingDetails) => {
  const validPricesMap: Record<string, SwarmPrice> = {};
  const validGiftPricesMap: Record<string, SwarmPrice> = {};

  // Make a hash of valid tiers downloaded on the client side
  const validTiersById: Record<string, SwarmTier> = (pricingData?.tiers || []).reduce(
    (acc, currentValue) => {
      acc[currentValue.id] = currentValue;

      // Map prices
      for (const price of currentValue.prices || []) {
        validPricesMap[price.id] = price;
      }
      return acc;
    },
    {} as Record<string, SwarmTier>,
  );

  // Make a hash of valid gift tiers downloaded on the client side
  const validGiftTiersById: Record<string, SwarmTier> = (pricingData?.gift_tiers || []).reduce(
    (acc, currentValue) => {
      acc[currentValue.id] = currentValue;

      // Map prices
      for (const price of currentValue.prices || []) {
        validGiftPricesMap[price.id] = price;
      }
      return acc;
    },
    {} as Record<string, SwarmTier>,
  );

  return produce(node, (draft) => {
    draft.attrs.data = (draft.attrs.data || [])
      .filter((tier) => {
        return tier.id === 'free' || validTiersById[tier.id];
      })
      .map((tier) => mapTier(tier, validTiersById, validPricesMap));

    // Update node and evaluate gift tiers by looking up any
    // gift tiers based on the tiers chosen in the website builder
    // by the user
    // Example;
    // 1. User created 3 tiers
    // 2. Tier 1 and 2 has gifting turned on
    // 3. User selects tier 1 and 3 in website builder
    // 4. We display "Tier 1" as a Tier to gift, but not tier 2
    // 5. We omit "Tier 2" because the user has not selected that
    //    tier in the website builder
    draft.attrs.giftTiersData = draft.attrs.data
      .filter((tier) => {
        return validGiftTiersById[tier.id];
      })
      .map((tier) => mapTier(tier, validGiftTiersById, validGiftPricesMap));
  });
};

const ErrorBoundaryFallback = ({ message }: { message: string }) => (
  <div className="flex flex-col max-w-2xl py-8 space-y-2">
    <Text as="p" type="body" size="md" className="leading-tight tracking-normal text-center">
      {message}
    </Text>
  </div>
);

const Pricing = ({ node, children }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { nextPage, nextStep } = useSignupRedirects();
  const { publication, site } = useRouteLoaderData('root') as RootLoaderData;
  const [searchParams] = useSearchParams();
  const hasOfferId = searchParams.has('offer_id');

  const {
    data: pricingData,
    isLoading: isPricingLoading,
    isError: hasPricingDataFetchError,
  } = usePricingDetails({ enabled: node.attrs.hasFakeData !== true });

  const canUseStripePaymentElement = publication?.uses_stripe_payment_element;

  const renderableNode = useMemo(() => {
    if (!isPricingLoading && !hasPricingDataFetchError && pricingData && node.attrs.hasFakeData !== true) {
      return swapTiersInNode(node, pricingData);
    }
    return node;
  }, [isPricingLoading, hasPricingDataFetchError, pricingData, node, node.attrs.hasFakeData]);

  const activeTiers = useMemo(
    () => (renderableNode.attrs.data || []).filter((tier) => !!tier.enabled) || [],
    [renderableNode.attrs.data],
  );
  const activeGiftTiers = useMemo(() => {
    try {
      return (renderableNode.attrs.giftTiersData || []).filter((tier) => !!tier.enabled) || [];
    } catch {
      return [];
    }
  }, [renderableNode.attrs.giftTiersData]);

  const canShowPaymentRequestButton =
    renderableNode.attrs.internalCheckout === true ? publication?.stripe_payment_method_domain_enabled : false;
  const isTermsOfServiceEnabled = !!site.viewable_site_version.settings.terms_of_service_enabled;
  const hasCustomTermsOfService = site.viewable_site_version.settings.terms_of_service;
  const isCollectingBillingAddress = renderableNode.attrs.collectBillingAddress || false;
  const isGiftingOnly = renderableNode.attrs.isGiftingOnly ?? false;

  if (hasPricingDataFetchError) {
    return null;
  }

  // When an offer is being applied, suppress rendering until pricing data is ready.
  // Without this, the component renders with the original (non-offer) prices from
  // the static page content and then flashes to the offer price once the fetch completes.
  if (isPricingLoading && hasOfferId && node.attrs.hasFakeData !== true) {
    return null;
  }

  return (
    <ErrorBoundary fallbackRender={(props) => <ErrorBoundaryFallback message={props.error.message} />}>
      <UpgradeProvider
        giftTiers={activeGiftTiers}
        tiers={activeTiers}
        isInternalCheckout={renderableNode.attrs.internalCheckout}
        isGiftingEnabled={renderableNode.attrs.isGiftingEnabled}
        isGiftingOnly={isGiftingOnly}
        recommendedTierId={renderableNode.attrs.recommendedTierId}
        recommendedPriceId={renderableNode.attrs.recommendedPriceId}
        nodeAttributes={renderableNode.attrs}
      >
        <UpgradeContext.Consumer>
          {(contextValue) => {
            // If the user has selected a free tier
            // or a price with premium offer that does
            // not require payment (while internal checkout is enabled)
            // then let them signup using signup action
            if (
              (contextValue.isInternalCheckout && contextValue.selectedTier?.id === 'free') ||
              (contextValue.isInternalCheckout &&
                contextValue.selectedPrice?.premium_offer &&
                !contextValue.selectedPrice?.premium_offer?.card_required)
            ) {
              return (
                <SignupAction
                  id={renderableNode.attrs.id}
                  premiumOfferId={contextValue.selectedPrice?.premium_offer?.id}
                  redirectUrl={contextValue.selectedTier?.redirect_url}
                >
                  {({ isLoading, setEmail }) => {
                    return (
                      <PricingElement
                        isCollectingBillingAddress={isCollectingBillingAddress}
                        element={renderableNode}
                        isInternalCheckout={renderableNode.attrs.internalCheckout}
                        selectedTier={contextValue.selectedTier}
                        onTierClick={contextValue.setSelectedTier}
                        selectedPrice={contextValue.selectedPrice}
                        onPriceClick={contextValue.setSelectedPrice}
                        isStripeLoading={false}
                        stripeSessionIds={{}}
                        email={contextValue.email}
                        onEmailChange={setEmail}
                        showTaxIdField={false}
                        taxId={contextValue.taxId}
                        onTaxIdChange={contextValue.setTaxId}
                        currentFormMode={contextValue.currentFormMode}
                        donationAmountCents={contextValue.donationAmountCents}
                        onDonationAmountCentsChange={contextValue.setDonationAmountCents}
                        isSubmitting={isLoading}
                        hasTermsOfService={isTermsOfServiceEnabled}
                        hasCustomTermsOfService={hasCustomTermsOfService}
                      >
                        {children}
                      </PricingElement>
                    );
                  }}
                </SignupAction>
              );
            }

            if (!contextValue.stripeSession) {
              return null;
            }

            // Handles the success of the upgrade action
            // 1. Redirect to the selected tier's redirect_url if it is defined
            // 2. Redirect to the next step if it is defined (signup flow)
            // 3. Redirect to the login URL if it is provided (SSO login)
            const handleSuccess = (loginUrl?: string) => {
              // Redirect to selected tier's redirect_url if it is defined
              const tierRedirectUrl =
                contextValue.currentFormMode === 'gift'
                  ? contextValue.selectedGiftTier?.redirect_url
                  : contextValue.selectedTier?.redirect_url;
              if (tierRedirectUrl && typeof window !== 'undefined') {
                window.location.href = tierRedirectUrl;
                return;
              }

              if (nextStep) {
                nextPage();
                return;
              }

              navigate(loginUrl || '/');
            };

            return (
              <Elements
                stripe={contextValue.stripeSession}
                options={canUseStripePaymentElement ? contextValue.stripeElementsOptions : undefined}
              >
                <UpgradeAction
                  canShowPaymentRequestButton={canShowPaymentRequestButton}
                  onSuccess={handleSuccess}
                  isCollectingBillingAddress={renderableNode.attrs.collectBillingAddress}
                >
                  {({ isSubmitting, paymentRequest, setEmail }) =>
                    contextValue.stripeSession ? (
                      <PricingElement
                        key={contextValue.currentFormMode}
                        isCollectingBillingAddress={isCollectingBillingAddress}
                        element={renderableNode}
                        isInternalCheckout={renderableNode.attrs.internalCheckout}
                        selectedTier={
                          contextValue.currentFormMode === 'gift'
                            ? contextValue.selectedGiftTier
                            : contextValue.selectedTier
                        }
                        onTierClick={
                          contextValue.currentFormMode === 'gift'
                            ? contextValue.setSelectedGiftTier
                            : contextValue.setSelectedTier
                        }
                        selectedPrice={
                          contextValue.currentFormMode === 'gift'
                            ? contextValue.selectedGiftPrice
                            : contextValue.selectedPrice
                        }
                        onPriceClick={
                          contextValue.currentFormMode === 'gift'
                            ? contextValue.setSelectedGiftPrice
                            : contextValue.setSelectedPrice
                        }
                        isStripeLoading={contextValue.isStripeLoading}
                        email={contextValue.email}
                        onEmailChange={setEmail}
                        showTaxIdField={contextValue.showTaxIdField}
                        taxId={contextValue.taxId}
                        onTaxIdChange={contextValue.setTaxId}
                        donationAmountCents={contextValue.donationAmountCents}
                        onDonationAmountCentsChange={contextValue.setDonationAmountCents}
                        isSubmitting={isSubmitting}
                        translations={{
                          'upgrade.tabs.gift.title': t('upgrade.tabs.gift.title'),
                          'upgrade.tabs.individual.title': t('upgrade.tabs.individual.title'),
                          'upgrade.tabs.gift.form.title': t('upgrade.tabs.gift.form.title', {
                            price: contextValue?.selectedGiftPrice
                              ? currencyFormatter({
                                  value: contextValue.selectedGiftPrice.amount_cents / 100,
                                  currency: contextValue.selectedGiftPrice.currency,
                                })
                              : '-',
                            interval: contextValue.selectedGiftPrice?.interval,
                            tier_name: contextValue?.selectedGiftTier?.name,
                          }),
                          'upgrade.tabs.gift.form.purchaser_email': t('upgrade.tabs.gift.form.purchaser_email'),
                          'upgrade.tabs.gift.form.purchaser_name': t('upgrade.tabs.gift.form.purchaser_name'),
                          'upgrade.tabs.gift.form.recipient_email': t('upgrade.tabs.gift.form.recipient_email'),
                          'upgrade.tabs.gift.form.message': t('upgrade.tabs.gift.form.message'),
                          'upgrade.tabs.gift.form.message_placeholder': t('upgrade.tabs.gift.form.message_placeholder'),
                          'upgrade.tabs.gift.form.start_at': t('upgrade.tabs.gift.form.start_at'),
                          'upgrade.weekly.per_week': t('upgrade.weekly.per_week'),
                          'upgrade.weekly.billed_as': t('upgrade.weekly.billed_as'),
                        }}
                        currentFormMode={contextValue.currentFormMode}
                        onCurrentFormModeChange={contextValue.setCurrentFormMode}
                        StripeCardElement={
                          !canUseStripePaymentElement && renderableNode.attrs.internalCheckout
                            ? (CardElement as StripeCardElementComponent)
                            : undefined
                        }
                        StripePaymentElement={
                          canUseStripePaymentElement && renderableNode.attrs.internalCheckout
                            ? PaymentElement
                            : undefined
                        }
                        PaymentRequestButtonElement={
                          !canUseStripePaymentElement && canShowPaymentRequestButton && paymentRequest
                            ? ((
                                <PaymentRequestButtonElement
                                  options={{
                                    paymentRequest,
                                  }}
                                />
                              ) as ReactNode)
                            : undefined
                        }
                        isLive
                      >
                        {children}
                      </PricingElement>
                    ) : null
                  }
                </UpgradeAction>
              </Elements>
            );
          }}
        </UpgradeContext.Consumer>
      </UpgradeProvider>
    </ErrorBoundary>
  );
};

export default Pricing;
